import { useReducer, useRef } from "react";
import { initialState, reducer } from "./state";
import { validatorEngine } from "helpers/functions";
import { ActionTypeReducer } from "helpers/types";
import axios from "axios";
import { useHistory } from 'react-router-dom';


type changeInputTypePass = ActionTypeReducer<typeof initialState.passForm>

export const useRecover = () => {

    const [state, dispatch] = useReducer(reducer, initialState);
    const history = useHistory();

    const cancelRef = useRef();

    const validatePassFields = () => {
        const { passForm } = state;
        const validations: validatorEngine.type<typeof initialState.passFormFieldsError> = {
            password: {
                test: () => validatorEngine.testString("required", passForm.password),
                errorMessage: "A senha é obrigatória"
            },
            passwordConfirm: {
                test: () => validateRepeatPassword(),
                errorMessage: "Senha precisa combinar com a senha anterior"
            },
        };

        const { isValid, errors } = validatorEngine.build(validations);

        dispatch({
            type: "setPassFormFieldsError",
            payload: { ...state.passFormFieldsError, ...errors }
        });

        return isValid;
    };


    const validateRepeatPassword = () => {
        const { password, passwordConfirm } = state.passForm;

        return password === passwordConfirm;
    }

    const displayInputError = (type: keyof typeof initialState.passFormFieldsError) => {
        const error = state.passFormFieldsError[type];

        return ({
            error: !!error,
            errorMessage: error
        })
    }

    const changeInput = ({ type, payload }: changeInputTypePass) => {
        const pass = { ...state.passForm, [type]: payload };
        dispatch({ type: "setPassForm", payload: pass });
    }

    const changeEmailInput = (payload: string) => {
        dispatch({ type: "setEmail", payload: payload });
    }

    const emailValidate = () => {
        const { email } = state;

        return validatorEngine.testString("email", email);
    }

    const sendEmail = async () => {
        if (!emailValidate()) {
            return;
        }

        dispatch({ type: "setLoading", payload: true });

        try {
            const response = await axios.post<boolean>(
                process.env.REACT_APP_API_URL + "/recuperar-senha/email-validacao",
                { email: state.email }
            );

            if (!response.data) {
                showError("Erro", "Email não encontrado");
                return;
            }

            dispatch({ type: "setStep", payload: "CODE" });
        } catch (e) {
            console.error("useRecover.sendEmail ", e);
            showError("Erro", "Erro ao enviar email de recuperação de senha");
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    }

    const changeCodeInput = (payload: string) => {
        dispatch({ type: "setCode", payload: payload });
    }

    const codeValidate = () => {
        const { code } = state;

        return validatorEngine.testString("minLength", code, 6);
    }

    const sendCode = async () => {
        if (!codeValidate()) {
            return;
        }

        dispatch({ type: "setLoading", payload: true });

        try {
            const response = await axios.post<boolean>(
                process.env.REACT_APP_API_URL + "/recuperar-senha/codigo-validacao",
                { email: state.email, codigo: state.code }
            );

            if (!response.data) {
                showError("Erro", "Código inválido");
                return;
            }

            dispatch({ type: "setStep", payload: "PASSWORD" });
        } catch (e) {
            console.error("useRecover.sendCode ", e);
            showError("Erro", "Erro ao enviar código de recuperação de senha");
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    }

    const backward = () => {
        dispatch({ type: "setStep", payload: "EMAIL" });
    }

    const savePass = async () => {
        if (!validatePassFields()) return;

        dispatch({ type: "setLoading", payload: true });

        try {
            await axios.post<boolean>(
                process.env.REACT_APP_API_URL + "/recuperar-senha/senha-alteracao",
                { email: state.email, senha: state.passForm.password, codigo: state.code }
            );

            history.push('/auth');
        } catch (error) {
            console.error("useRecover.savePass ", error);
            showError("Erro", "Erro ao salvar nova senha");
        } finally {
            dispatch({ type: "setLoading", payload: false });
        }
    }

    const showError = (title: string, msg: string) => {
        dispatch({ type: "setError", payload: true });
        dispatch({ type: "setErrorTitle", payload: title });
        dispatch({ type: "setErrorMsg", payload: msg });
    };

    const closeError = () => {
        dispatch({ type: "setError", payload: false });
    };

    return {
        state,
        changeEmailInput,
        emailValidate,
        sendEmail,
        changeCodeInput,
        codeValidate,
        sendCode,
        backward,
        changeInput,
        displayInputError,
        savePass,
        closeError,
        cancelRef
    };
};

export namespace useRecover {
    export type type = ReturnType<typeof useRecover>;
}